'use client';

import { useMemo } from 'react';
import { useContextSelector } from 'use-context-selector';
import {
  MountedProjectsContext,
  type MountedProjectsContextValue,
} from '../contexts/MountedProjectsContextProvider';

function requireContext(value: MountedProjectsContextValue | null) {
  if (!value) throw new Error('MountedProjectsContextProvider not mounted');
  return value;
}

export function useMountedProjects() {
  return useContextSelector(
    MountedProjectsContext,
    (value) => requireContext(value).state
  );
}

/**
 * Mount state for one project. Unlike the other mounted-project hooks, this
 * does not require `MountedProjectsContextProvider`: without it the project
 * reads as unmounted with an `unavailable` status, so shared UI such as
 * project cards can render outside the main app (for example in Storybook).
 */
export function useIsProjectMounted(projectId: string | null | undefined) {
  const isMounted = useContextSelector(
    MountedProjectsContext,
    (value) =>
      projectId != null && Boolean(value?.state.projectIds.includes(projectId))
  );
  const loading = useContextSelector(
    MountedProjectsContext,
    (value) => value?.state.loading ?? false
  );
  const error = useContextSelector(
    MountedProjectsContext,
    (value) => value?.state.error ?? null
  );
  const status = useContextSelector(
    MountedProjectsContext,
    (value) => value?.state.status ?? 'unavailable'
  );
  return useMemo(
    () => ({ isMounted, loading, error, status }),
    [isMounted, loading, error, status]
  );
}

/**
 * Drive mount status of a project:
 * - `unknown`: messaging is off or unavailable, Drive is not connected, or the
 *   mounted list has not loaded yet.
 * - `unmounted`: Drive is connected and the project is not mounted.
 * - `mounting`: Drive is mounting the project.
 * - `mounted`: Drive has the project mounted.
 * - `ejecting`: Drive is ejecting the project.
 */
export type ProjectMountStatus =
  | 'unknown'
  | 'unmounted'
  | 'mounting'
  | 'mounted'
  | 'ejecting';

/**
 * Drive mount status for one project. Like `useIsProjectMounted`, this does
 * not require `MountedProjectsContextProvider`; without it the status is
 * `unknown`.
 */
export function useProjectMountStatus(
  projectId: string | null | undefined
): ProjectMountStatus {
  return useContextSelector(MountedProjectsContext, (value) => {
    if (!value || projectId == null) return 'unknown';
    const { projectIds, pendingProjectIds, ejectingProjectIds, status } =
      value.state;
    if (projectIds.includes(projectId)) return 'mounted';
    if (pendingProjectIds.includes(projectId)) return 'mounting';
    if (ejectingProjectIds.includes(projectId)) return 'ejecting';
    return status === 'ready' ? 'unmounted' : 'unknown';
  });
}

const unavailableMountActions: MountedProjectsContextValue['actions'] = {
  mountProject: () =>
    Promise.reject(new Error('MountedProjectsContextProvider not mounted')),
  ejectProject: () =>
    Promise.reject(new Error('MountedProjectsContextProvider not mounted')),
};

/**
 * Mount and eject actions. Without `MountedProjectsContextProvider` the
 * actions reject instead of throwing on render, matching
 * `useIsProjectMounted`.
 */
export function useProjectMountActions() {
  return useContextSelector(
    MountedProjectsContext,
    (value) => value?.actions ?? unavailableMountActions
  );
}
